import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { Check, LockKeyhole, Plus, RefreshCw, Save, ShieldCheck, SlidersHorizontal, Users } from 'lucide-react';
import { useAuth } from '@/core/hooks';

interface AccessRestrictions {
  branchIds: string[];
  clientIds: string[];
  carerIds: string[];
}

interface PermissionItem {
  id: string;
  label: string;
  group: string;
}

interface SelectOption {
  id: string;
  name: string;
}

interface RoleDefinition {
  id: string;
  name: string;
  description: string;
  system: boolean;
  permissions: string[];
  restrictions: AccessRestrictions;
}

interface AccessUser {
  id: string;
  name: string;
  email: string;
  roles: string[];
  permissions: string[];
  directPermissions: string[];
  accessRestrictions: AccessRestrictions;
  disabled: boolean;
}

interface RoleAccessPayload {
  roles: RoleDefinition[];
  users: AccessUser[];
  permissions: PermissionItem[];
  branches: SelectOption[];
  clients: SelectOption[];
  carers: SelectOption[];
}

const emptyRestrictions: AccessRestrictions = { branchIds: [], clientIds: [], carerIds: [] };

function normalizeCustomRoleName(value: string) {
  return value.trim().toUpperCase().replace(/[^A-Z0-9]+/g, '_').replace(/^_+|_+$/g, '');
}

function toggleValue(items: string[], value: string) {
  return items.includes(value) ? items.filter((item) => item !== value) : [...items, value];
}

function groupedPermissions(permissions: PermissionItem[]) {
  return permissions.reduce<Record<string, PermissionItem[]>>((groups, permission) => {
    const group = permission.group || 'General';
    groups[group] = [...(groups[group] || []), permission];
    return groups;
  }, {});
}

export const RoleAccessManagement: React.FC = () => {
  const { token, user } = useAuth();
  const [data, setData] = useState<RoleAccessPayload | null>(null);
  const [selectedRoleId, setSelectedRoleId] = useState('ADMIN');
  const [selectedUserId, setSelectedUserId] = useState('');
  const [newRoleName, setNewRoleName] = useState('');
  const [serverAdminToken, setServerAdminToken] = useState('');
  const [loadError, setLoadError] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  const permissionGroups = useMemo(() => groupedPermissions(data?.permissions || []), [data?.permissions]);
  const selectedRole = data?.roles.find((role) => role.id === selectedRoleId) || data?.roles[0] || null;
  const selectedUser = data?.users.find((item) => item.id === selectedUserId) || data?.users[0] || null;

  const loadRoles = async () => {
    if (!token && !serverAdminToken.trim()) {
      setIsLoading(false);
      setLoadError('Sign in as administrator or enter the server admin token.');
      return;
    }
    setIsLoading(true);
    setLoadError('');
    try {
      const response = await fetch('/api/auth/roles', {
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          ...(serverAdminToken.trim() ? { 'X-Admin-Config-Token': serverAdminToken.trim() } : {}),
        },
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload?.error || 'Could not load roles');
      setData(payload.data);
      setSelectedRoleId((current) => payload.data.roles.some((role: RoleDefinition) => role.id === current) ? current : payload.data.roles[0]?.id || '');
      setSelectedUserId((current) => payload.data.users.some((item: AccessUser) => item.id === current) ? current : payload.data.users[0]?.id || '');
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Could not load roles';
      setLoadError(message);
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    void loadRoles();
  }, [token]);

  const updateRole = (roleId: string, updater: (role: RoleDefinition) => RoleDefinition) => {
    setData((current) => {
      if (!current) return current;
      return {
        ...current,
        roles: current.roles.map((role) => (role.id === roleId ? updater(role) : role)),
      };
    });
  };

  const updateUser = (userId: string, updater: (accessUser: AccessUser) => AccessUser) => {
    setData((current) => {
      if (!current) return current;
      return {
        ...current,
        users: current.users.map((accessUser) => (accessUser.id === userId ? updater(accessUser) : accessUser)),
      };
    });
  };

  const addCustomRole = () => {
    if (!data) return;
    const id = normalizeCustomRoleName(newRoleName);
    if (!id) {
      toast.error('Enter a role name first.');
      return;
    }
    if (data.roles.some((role) => role.id === id)) {
      toast.error('This role already exists.');
      return;
    }

    const nextRole: RoleDefinition = {
      id,
      name: newRoleName.trim(),
      description: 'Custom role created by the system owner.',
      system: false,
      permissions: [],
      restrictions: emptyRestrictions,
    };
    setData({ ...data, roles: [...data.roles, nextRole] });
    setSelectedRoleId(id);
    setNewRoleName('');
  };

  const save = async () => {
    if ((!token && !serverAdminToken.trim()) || !data) return;
    setIsSaving(true);
    try {
      const response = await fetch('/api/auth/roles', {
        method: 'POST',
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          ...(serverAdminToken.trim() ? { 'X-Admin-Config-Token': serverAdminToken.trim() } : {}),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ roles: data.roles, users: data.users }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload?.error || 'Could not save roles');
      setData((current) => current ? { ...current, ...payload.data } : payload.data);
      toast.success('Roles, permissions and restrictions saved.');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not save roles');
    } finally {
      setIsSaving(false);
    }
  };

  const renderRestrictionPicker = (
    title: string,
    options: SelectOption[],
    values: string[],
    onChange: (nextValues: string[]) => void
  ) => (
    <section className="rounded-lg border border-slate-200 bg-white p-4">
      <div className="mb-3 flex items-center justify-between gap-3">
        <h3 className="text-sm font-semibold text-slate-950">{title}</h3>
        <span className="text-xs font-medium text-slate-500">{values.length ? `${values.length} selected` : 'All allowed'}</span>
      </div>
      <div className="grid gap-2 sm:grid-cols-2">
        {options.map((option) => (
          <label key={option.id} className="flex items-center gap-2 rounded-md border border-slate-200 px-3 py-2 text-sm text-slate-700">
            <input
              type="checkbox"
              checked={values.includes(option.id)}
              onChange={() => onChange(toggleValue(values, option.id))}
              className="h-4 w-4 accent-[#6c3a80]"
            />
            <span>{option.name}</span>
          </label>
        ))}
      </div>
      <p className="mt-3 text-xs leading-5 text-slate-500">
        Leave empty to allow every {title.toLowerCase()}. Select one or more to restrict access.
      </p>
    </section>
  );

  if (isLoading) {
    return (
      <div className="flex min-h-[420px] items-center justify-center">
        <RefreshCw className="h-6 w-6 animate-spin text-[#6c3a80]" />
      </div>
    );
  }

  if (!data || !selectedRole || !selectedUser) {
    return (
      <div className="mx-auto max-w-2xl space-y-4 px-4 py-10">
        <div className="rounded-lg border border-red-200 bg-red-50 p-6 text-red-700">
          <h1 className="text-xl font-bold text-red-900">Roles and access data could not be loaded</h1>
          <p className="mt-2 text-sm">{loadError || 'The administrator session may have expired.'}</p>
        </div>
        <div className="rounded-lg border border-slate-200 bg-white p-5">
          <label className="block space-y-2">
            <span className="text-sm font-semibold text-slate-900">Server admin token</span>
            <input
              value={serverAdminToken}
              onChange={(event) => setServerAdminToken(event.target.value)}
              type="password"
              placeholder="Enter the VPS admin configuration token"
              className="h-11 w-full rounded-md border border-slate-300 px-3 text-sm outline-none focus:border-[#6c3a80] focus:ring-2 focus:ring-[#6c3a80]/20"
            />
          </label>
          <button
            type="button"
            onClick={loadRoles}
            className="mt-4 inline-flex h-10 items-center gap-2 rounded-md bg-[#6c3a80] px-4 text-sm font-semibold text-white hover:bg-[#5e3474]"
          >
            <RefreshCw className="h-4 w-4" />
            Load roles
          </button>
        </div>
      </div>
    );
  }

  const canSaveAccess = Boolean(user?.roles.includes('SUPER_ADMIN') || serverAdminToken.trim());

  return (
    <div className="mx-auto max-w-7xl space-y-6 px-4 py-6 sm:px-6 lg:px-8">
      <header className="flex flex-col gap-4 border-b border-slate-200 pb-5 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <div className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-[#6c3a80]">
            <ShieldCheck className="h-4 w-4" />
            Security controls
          </div>
          <h1 className="mt-2 text-3xl font-bold text-slate-950">Roles & Access</h1>
          <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600">
            Configure detailed role-based access for Admin, Coordinator, Manager, Carer, Family and custom roles. Restrictions can limit users by branch, client or carer.
          </p>
        </div>
        <div className="flex flex-wrap gap-3">
          <button
            type="button"
            onClick={loadRoles}
            className="inline-flex h-10 items-center gap-2 rounded-md border border-slate-300 bg-white px-4 text-sm font-semibold text-slate-700 hover:bg-slate-50"
          >
            <RefreshCw className="h-4 w-4" />
            Refresh
          </button>
          <button
            type="button"
            onClick={save}
            disabled={!canSaveAccess || isSaving}
            className="inline-flex h-10 items-center gap-2 rounded-md bg-[#6c3a80] px-4 text-sm font-semibold text-white hover:bg-[#5e3474] disabled:cursor-not-allowed disabled:opacity-60"
          >
            <Save className="h-4 w-4" />
            {isSaving ? 'Saving...' : 'Save access'}
          </button>
        </div>
      </header>

      {!canSaveAccess && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
          Only the system owner can save role and restriction changes. Admin can review this page.
        </div>
      )}

      <section className="grid gap-4 lg:grid-cols-[320px_1fr]">
        <aside className="space-y-4">
          <div className="rounded-lg border border-slate-200 bg-white p-4">
            <div className="mb-3 flex items-center gap-2">
              <LockKeyhole className="h-4 w-4 text-[#6c3a80]" />
              <h2 className="text-sm font-semibold text-slate-950">Roles</h2>
            </div>
            <div className="space-y-2">
              {data.roles.map((role) => (
                <button
                  key={role.id}
                  type="button"
                  onClick={() => setSelectedRoleId(role.id)}
                  className={`w-full rounded-md border px-3 py-2 text-left text-sm transition ${
                    selectedRoleId === role.id
                      ? 'border-[#6c3a80] bg-[#fbf7fd] text-[#5e3474]'
                      : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
                  }`}
                >
                  <span className="flex items-center justify-between gap-2">
                    <span className="font-semibold">{role.name}</span>
                    {role.system && <span className="text-[11px] uppercase text-slate-400">System</span>}
                  </span>
                  <span className="mt-1 block text-xs text-slate-500">{role.permissions.includes('*') ? 'All permissions' : `${role.permissions.length} permissions`}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="rounded-lg border border-slate-200 bg-white p-4">
            <h2 className="text-sm font-semibold text-slate-950">Create custom role</h2>
            <div className="mt-3 flex gap-2">
              <input
                value={newRoleName}
                onChange={(event) => setNewRoleName(event.target.value)}
                placeholder="Example: Branch Auditor"
                className="h-10 min-w-0 flex-1 rounded-md border border-slate-300 px-3 text-sm outline-none focus:border-[#6c3a80] focus:ring-2 focus:ring-[#6c3a80]/20"
              />
              <button
                type="button"
                onClick={addCustomRole}
                className="inline-flex h-10 items-center justify-center rounded-md bg-slate-900 px-3 text-white hover:bg-slate-800"
                aria-label="Create role"
              >
                <Plus className="h-4 w-4" />
              </button>
            </div>
          </div>
        </aside>

        <main className="space-y-4">
          <section className="rounded-lg border border-slate-200 bg-white p-5">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
              <div className="grid flex-1 gap-3 sm:grid-cols-2">
                <label className="space-y-1">
                  <span className="text-xs font-semibold uppercase text-slate-500">Role name</span>
                  <input
                    value={selectedRole.name}
                    onChange={(event) => updateRole(selectedRole.id, (role) => ({ ...role, name: event.target.value }))}
                    className="h-10 w-full rounded-md border border-slate-300 px-3 text-sm outline-none focus:border-[#6c3a80] focus:ring-2 focus:ring-[#6c3a80]/20"
                  />
                </label>
                <label className="space-y-1">
                  <span className="text-xs font-semibold uppercase text-slate-500">Role ID</span>
                  <input
                    value={selectedRole.id}
                    disabled
                    className="h-10 w-full rounded-md border border-slate-200 bg-slate-50 px-3 text-sm text-slate-500"
                  />
                </label>
              </div>
              <div className="rounded-md bg-slate-50 px-3 py-2 text-sm text-slate-600">
                <span className="font-semibold text-slate-900">{selectedRole.permissions.length}</span> active permissions
              </div>
            </div>
            <label className="mt-4 block space-y-1">
              <span className="text-xs font-semibold uppercase text-slate-500">Description</span>
              <textarea
                value={selectedRole.description}
                onChange={(event) => updateRole(selectedRole.id, (role) => ({ ...role, description: event.target.value }))}
                rows={3}
                className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-[#6c3a80] focus:ring-2 focus:ring-[#6c3a80]/20"
              />
            </label>
          </section>

          <section className="rounded-lg border border-slate-200 bg-white p-5">
            <div className="mb-4 flex items-center gap-2">
              <SlidersHorizontal className="h-4 w-4 text-[#6c3a80]" />
              <h2 className="text-sm font-semibold text-slate-950">Role permissions</h2>
            </div>
            <div className="grid gap-4 xl:grid-cols-2">
              {Object.entries(permissionGroups).map(([group, permissions]) => (
                <div key={group} className="rounded-lg border border-slate-200 p-4">
                  <h3 className="mb-3 text-sm font-semibold text-slate-900">{group}</h3>
                  <div className="space-y-2">
                    {permissions.map((permission) => (
                      <label key={permission.id} className="flex items-start gap-2 text-sm text-slate-700">
                        <input
                          type="checkbox"
                          checked={selectedRole.permissions.includes(permission.id)}
                          onChange={() => updateRole(selectedRole.id, (role) => ({ ...role, permissions: toggleValue(role.permissions, permission.id) }))}
                          className="mt-0.5 h-4 w-4 accent-[#6c3a80]"
                        />
                        <span>
                          <span className="font-medium text-slate-900">{permission.label}</span>
                          <span className="block text-xs text-slate-500">{permission.id}</span>
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section className="rounded-lg border border-slate-200 bg-white p-5">
            <div className="mb-4 flex items-center gap-2">
              <Users className="h-4 w-4 text-[#6c3a80]" />
              <h2 className="text-sm font-semibold text-slate-950">User role assignment</h2>
            </div>
            <div className="grid gap-4 lg:grid-cols-[280px_1fr]">
              <div className="space-y-2">
                {data.users.map((accessUser) => (
                  <button
                    key={accessUser.id}
                    type="button"
                    onClick={() => setSelectedUserId(accessUser.id)}
                    className={`w-full rounded-md border px-3 py-2 text-left text-sm ${
                      selectedUser.id === accessUser.id
                        ? 'border-[#6c3a80] bg-[#fbf7fd]'
                        : 'border-slate-200 bg-white hover:bg-slate-50'
                    }`}
                  >
                    <span className="font-semibold text-slate-950">{accessUser.name}</span>
                    <span className="block text-xs text-slate-500">{accessUser.email}</span>
                  </button>
                ))}
              </div>

              <div className="space-y-4">
                <div className="rounded-lg border border-slate-200 p-4">
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                      <h3 className="text-base font-semibold text-slate-950">{selectedUser.name}</h3>
                      <p className="text-sm text-slate-500">{selectedUser.email}</p>
                    </div>
                    <div className="flex flex-wrap gap-3 text-sm">
                      <label className="inline-flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={selectedUser.disabled}
                          onChange={() => updateUser(selectedUser.id, (item) => ({ ...item, disabled: !item.disabled }))}
                          className="h-4 w-4 accent-red-600"
                        />
                        Disabled
                      </label>
                    </div>
                  </div>

                  <div className="mt-4 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                    {data.roles.map((role) => (
                      <label key={role.id} className="flex items-center gap-2 rounded-md border border-slate-200 px-3 py-2 text-sm text-slate-700">
                        <input
                          type="checkbox"
                          checked={selectedUser.roles.includes(role.id)}
                          onChange={() => updateUser(selectedUser.id, (item) => ({ ...item, roles: toggleValue(item.roles, role.id) }))}
                          className="h-4 w-4 accent-[#6c3a80]"
                        />
                        <span>{role.name}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div className="grid gap-4 xl:grid-cols-3">
                  {renderRestrictionPicker('Branches', data.branches, selectedUser.accessRestrictions.branchIds, (branchIds) =>
                    updateUser(selectedUser.id, (item) => ({ ...item, accessRestrictions: { ...item.accessRestrictions, branchIds } }))
                  )}
                  {renderRestrictionPicker('Clients', data.clients, selectedUser.accessRestrictions.clientIds, (clientIds) =>
                    updateUser(selectedUser.id, (item) => ({ ...item, accessRestrictions: { ...item.accessRestrictions, clientIds } }))
                  )}
                  {renderRestrictionPicker('Carers', data.carers, selectedUser.accessRestrictions.carerIds, (carerIds) =>
                    updateUser(selectedUser.id, (item) => ({ ...item, accessRestrictions: { ...item.accessRestrictions, carerIds } }))
                  )}
                </div>

                <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600">
                  <div className="mb-2 flex items-center gap-2 font-semibold text-slate-900">
                    <Check className="h-4 w-4 text-emerald-600" />
                    Effective permissions
                  </div>
                  <p className="leading-6">
                    {selectedUser.permissions.includes('*') ? 'Full system access.' : selectedUser.permissions.join(', ')}
                  </p>
                </div>
              </div>
            </div>
          </section>
        </main>
      </section>
    </div>
  );
};
